import React, { createContext, useContext, useState } from 'react';
import Yup from '~/util/yup';
import {
	DisciplineEnum,
	getLegacyCurrentActiveChartProfileOptions,
	LegacyReferralAreaEnum,
	LegacyReferralAreaOptions,
	newLegacyReferralOptions,
	type Currentactivechartprofile,
	type LegacyCurrentActiveChartProfileViewModel,
	type SearchLegacyChartProfilesViewModel
} from '@generated';
import { FormModal } from '~/shared/forms/FormModal';
import type { FormValues } from '~/shared/forms/types';
import { getSearchQueryKey, useSearch } from '~/util/http/useSearch';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import { addDays } from 'date-fns';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { LegacyClinicianAutocomplete } from './assignments/LegacyClinicianAutocomplete';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { Button } from '~/shared/components/buttons/Button';
import { MapPin, Search, UserPlus } from 'lucide-react';
import { useAzureMapsGeocode } from '~/shared/maps/useAzureMapsGeocode';
import { useForm } from '~/shared/forms/FormContext';
import { TextAreaFormField } from '~/shared/forms/controls/fields/text-fields/TextAreaFormField';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

const PatientDataContext = createContext<Currentactivechartprofile | undefined>(undefined);

const validationSchema = Yup.object().shape({
	address: Yup.object().address().label('Address').required().nullable(),
	expectedStartOfCareDate: Yup.date().label('Expected Start of Care Date').required().nullable(),
	diagnosis: Yup.string().label('Diagnosis').nullable(),
	snNeeded: Yup.boolean().label('Skilled Nursing Needed').required(),
	ptNeeded: Yup.boolean().label('Physical Therapy Needed').required(),
	stNeeded: Yup.boolean().label('Speech Therapy Needed').required(),
	otNeeded: Yup.boolean().label('Occupational Therapy Needed').required(),
	mswNeeded: Yup.boolean().label('Medical Social Worker Needed').required(),
	hhaNeeded: Yup.boolean().label('Home Health Aide Needed').required(),
	mrNumber: Yup.string().label('MR Number').required().nullable(),
	existingPatient: Yup.boolean().label('Existing Patient').required(),
	patientFirstName: Yup.string()
		.label('First Name')
		.when('existingPatient', {
			is: false,
			then: schema => schema.required(),
			otherwise: schema => schema.nullable()
		}),
	patientLastName: Yup.string()
		.label('Last Name')
		.when('existingPatient', {
			is: false,
			then: schema => schema.required(),
			otherwise: schema => schema.nullable()
		}),
	snStartAssignId: Yup.string().label('SN SOC Assign').nullable(),
	snPrimaryCareAssignId: Yup.string().label('SN Primary Care Assign').nullable(),
	ptAssignId: Yup.string().label('PT Assign').nullable(),
	stAssignId: Yup.string().label('ST Assign').nullable(),
	otAssignId: Yup.string().label('OT Assign').nullable(),
	mswAssignId: Yup.string().label('MSW Assign').nullable(),
	hhaAssignId: Yup.string().label('HHA Assign').nullable(),
	accepted: Yup.boolean().label('Accepted').required(),
	referralNotes: Yup.string().label('Referral Notes').nullable(),
	priority: Yup.boolean().label('Priority').nullable(),
	areaId: Yup.number().label('Area').nullable().required(),
	disableTeamsMessages: Yup.boolean().label('Disable Teams Messages').nullable()
});

const areaOptions = LegacyReferralAreaOptions.filter(x => x.id !== LegacyReferralAreaEnum.All);

const disciplineFields = [
	{ name: 'snNeeded', disciplineId: DisciplineEnum.SkilledNursing, assignFields: ['snStartAssignId', 'snPrimaryCareAssignId'] },
	{ name: 'ptNeeded', disciplineId: DisciplineEnum.PhysicalTherapy, assignFields: ['ptAssignId'] },
	{ name: 'stNeeded', disciplineId: DisciplineEnum.SpeechTherapy, assignFields: ['stAssignId'] },
	{ name: 'otNeeded', disciplineId: DisciplineEnum.OccupationalTherapy, assignFields: ['otAssignId'] },
	{ name: 'mswNeeded', disciplineId: DisciplineEnum.MedicalSocialWorker, assignFields: ['mswAssignId'] },
	{ name: 'hhaNeeded', disciplineId: DisciplineEnum.HomeHealthAide, assignFields: ['hhaAssignId'] }
];

function toNewLegacyReferralCommand(values: FormValues): FormValues {
	const command: FormValues = { ...values, disciplinesNeeded: [] };
	disciplineFields.forEach(x => {
		if (values[x.name]) {
			command.disciplinesNeeded.push(x.disciplineId);
		} else {
			x.assignFields.forEach(y => (command[y] = null));
		}
		delete command[x.name];
	});
	return command;
}

export function NewLegacyReferralFormModal({ closeModal }: { closeModal: () => void }) {
	return (
		<FormModal
			title="New Referral"
			closeModal={closeModal}
			initialValues={{
				address: null,
				expectedStartOfCareDate: addDays(new Date(), 1),
				diagnosis: null,
				snNeeded: false,
				ptNeeded: false,
				stNeeded: false,
				otNeeded: false,
				mswNeeded: false,
				hhaNeeded: false,
				mrNumber: null,
				existingPatient: false,
				patientFirstName: null,
				patientLastName: null,
				snStartAssignId: null,
				snPrimaryCareAssignId: null,
				ptAssignId: null,
				stAssignId: null,
				otAssignId: null,
				mswAssignId: null,
				hhaAssignId: null,
				accepted: false,
				referralNotes: null,
				priority: false,
				areaId: null,
				disableTeamsMessages: false
			}}
			onSaveTransform={toNewLegacyReferralCommand}
			saveMutation={{
				...newLegacyReferralOptions,
				invalidateQueryKeys: [getSearchQueryKey('legacy-referral')]
			}}
			onSuccess={closeModal}
			validationSchema={validationSchema}
		>
			{formHelpers => {
				return (
					<div className="grid grid-cols-1 gap-8">
						<LegacyReferralPatientInfo>
							<SelectFormField name="areaId" label="Area" options={areaOptions} />
							<TextFormField name="diagnosis" />
							<div className="grid grid-cols-1 gap-4">
								<CheckboxFormField name="snNeeded" label="Skilled Nursing Needed" hideLabel />
								{formHelpers.values.snNeeded && (
									<>
										<PreviousAssignmentInfo
											label="RN"
											emailKey="rnemail"
											nameKey="rnname"
											assignableKey="rnassignable"
											onAssign={userId => {
												formHelpers.setFieldValue('snStartAssignId', userId);
												formHelpers.setFieldValue('snPrimaryCareAssignId', userId);
											}}
										/>
										<PreviousAssignmentInfo
											label="LPN"
											emailKey="lpnemail"
											nameKey="lpnname"
											assignableKey="lpnassignable"
											onAssign={userId => formHelpers.setFieldValue('snPrimaryCareAssignId', userId)}
										/>
										<div className="grid grid-cols-2 gap-4">
											<LegacyClinicianAutocomplete
												name="snStartAssignId"
												label="SN SOC"
												disciplineId={DisciplineEnum.SkilledNursing}
												startOfCare
											/>
											<LegacyClinicianAutocomplete
												name="snPrimaryCareAssignId"
												label="SN Primary Care"
												disciplineId={DisciplineEnum.SkilledNursing}
											/>
										</div>
									</>
								)}
								<CheckboxFormField name="ptNeeded" label="Physical Therapy Needed" hideLabel />
								{formHelpers.values.ptNeeded && (
									<>
										<PreviousAssignmentInfo
											label="PT"
											emailKey="ptemail"
											nameKey="ptname"
											assignableKey="ptassignable"
											onAssign={userId => formHelpers.setFieldValue('ptAssignId', userId)}
										/>
										<LegacyClinicianAutocomplete name="ptAssignId" label="PT" disciplineId={DisciplineEnum.PhysicalTherapy} />
									</>
								)}
								<CheckboxFormField name="stNeeded" label="Speech Therapy Needed" hideLabel />
								{formHelpers.values.stNeeded && (
									<>
										<PreviousAssignmentInfo
											label="ST"
											emailKey="stemail"
											nameKey="stname"
											assignableKey="stassignable"
											onAssign={userId => formHelpers.setFieldValue('stAssignId', userId)}
										/>
										<LegacyClinicianAutocomplete name="stAssignId" label="ST" disciplineId={DisciplineEnum.SpeechTherapy} />
									</>
								)}
								<CheckboxFormField name="otNeeded" label="Occupational Therapy Needed" hideLabel />
								{formHelpers.values.otNeeded && (
									<>
										<PreviousAssignmentInfo
											label="OT"
											emailKey="otemail"
											nameKey="otname"
											assignableKey="otassignable"
											onAssign={userId => formHelpers.setFieldValue('otAssignId', userId)}
										/>
										<LegacyClinicianAutocomplete name="otAssignId" label="OT" disciplineId={DisciplineEnum.OccupationalTherapy} />
									</>
								)}
								<CheckboxFormField name="mswNeeded" label="Medical Social Worker Needed" hideLabel />
								{formHelpers.values.mswNeeded && (
									<>
										<PreviousAssignmentInfo
											label="MSW"
											emailKey="mswemail"
											nameKey="mswname"
											assignableKey="mswassignable"
											onAssign={userId => formHelpers.setFieldValue('mswAssignId', userId)}
										/>
										<LegacyClinicianAutocomplete
											name="mswAssignId"
											label="MSW"
											disciplineId={DisciplineEnum.MedicalSocialWorker}
										/>
									</>
								)}
								<CheckboxFormField name="hhaNeeded" label="Home Health Aide Needed" hideLabel />
								{formHelpers.values.hhaNeeded && (
									<>
										<PreviousAssignmentInfo
											label="HHA"
											emailKey="hhaemail"
											nameKey="hhaname"
											assignableKey="hhaassignable"
											onAssign={userId => formHelpers.setFieldValue('hhaAssignId', userId)}
										/>
										<LegacyClinicianAutocomplete name="hhaAssignId" label="HHA" disciplineId={DisciplineEnum.HomeHealthAide} />
									</>
								)}
							</div>

							<CheckboxFormField name="accepted" label="Accepted" hideLabel />
							<CheckboxFormField name="priority" label="Priority (ignore radius)" hideLabel />
							<CheckboxFormField name="disableTeamsMessages" label="Disable Teams Messages" hideLabel />
							<TextAreaFormField name="referralNotes" />
						</LegacyReferralPatientInfo>
					</div>
				);
			}}
		</FormModal>
	);
}

interface PreviousAssignmentInfoProps {
	label: string;
	emailKey: keyof SearchLegacyChartProfilesViewModel;
	nameKey: keyof SearchLegacyChartProfilesViewModel;
	assignableKey: keyof SearchLegacyChartProfilesViewModel;
	onAssign: (userId: string) => void;
}

function PreviousAssignmentInfo(props: PreviousAssignmentInfoProps) {
	const patientData = useContext(PatientDataContext);
	if (!patientData) return null;
	return <PreviousAssignment {...props} patientData={patientData} />;
}

function PreviousAssignment({
	label,
	emailKey,
	nameKey,
	assignableKey,
	onAssign,
	patientData
}: PreviousAssignmentInfoProps & { patientData: Currentactivechartprofile }) {
	const email = patientData[emailKey] as string | undefined;
	const name = patientData[nameKey] as string | undefined;
	const assignable = patientData[assignableKey] as boolean | undefined;
	const { data: users, isLoading } = useSearch({
		searchType: 'user',
		filters: [{ name: 'emailAddress', operator: '==', value: email ?? '' }],
		pageSize: 1,
		enabled: !!email && !!name && !!assignable
	});
	const duroUser = users?.[0];

	if (!email) {
		return (
			<div className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
				<span className="font-medium">{label}:</span> No previous assignment
			</div>
		);
	}

	if (!name || !assignable || (!isLoading && !duroUser)) {
		return (
			<div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
				<span className="font-medium">{label}:</span> {name ?? email} — not currently available for assignment
			</div>
		);
	}

	return (
		<div className="flex items-center gap-2 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
			<span className="font-medium">{label}:</span> {name}
			{duroUser && (
				<Button intent="success" size="xs" className="ml-auto" Icon={UserPlus} label="Assign" onClick={() => onAssign(duroUser.id)} />
			)}
		</div>
	);
}

type PatientLookupStatus = 'idle' | 'found' | 'not-found' | 'error';

function LegacyReferralPatientInfo({ children }: { children: React.ReactNode }) {
	const { formHelpers } = useForm();
	const queryClient = useQueryClient();
	const [isLookingUp, setIsLookingUp] = useState(false);
	const [patientLookupMR, setPatientLookupMR] = useState<string | null>(null);
	const [patientLookupStatus, setPatientLookupStatus] = useState<PatientLookupStatus>('idle');
	const [patientLookupResults, setPatientLookupResults] = useState<LegacyCurrentActiveChartProfileViewModel | null>(null);

	const clearPatient = () => {
		formHelpers.setFieldValue('patientFirstName', null);
		formHelpers.setFieldValue('patientLastName', null);
		formHelpers.setFieldValue('existingPatient', false);
	};

	const onMrNumberChange = () => {
		if (patientLookupStatus === 'idle') return;
		setPatientLookupStatus('idle');
		setPatientLookupResults(null);
		clearPatient();
	};

	const onPatientLookup = async (mrNumber: string) => {
		setPatientLookupMR(mrNumber);
		setIsLookingUp(true);
		try {
			const result = await queryClient.fetchQuery({ ...getLegacyCurrentActiveChartProfileOptions(mrNumber), staleTime: 0 });
			setPatientLookupStatus(result.chartFound ? 'found' : 'not-found');
			setPatientLookupResults(result);
			if (result.patient) {
				formHelpers.setFieldValue('patientFirstName', result.patient.firstName);
				formHelpers.setFieldValue('patientLastName', result.patient.lastName);
				formHelpers.setFieldValue('existingPatient', true);
			} else {
				clearPatient();
			}
		} catch {
			setPatientLookupStatus('error');
			clearPatient();
		} finally {
			setIsLookingUp(false);
		}
	};

	const geocode = useAzureMapsGeocode();

	const onUseExistingAddress = () => {
		if (patientLookupStatus !== 'found' || !patientLookupResults?.patient?.address) return;
		geocode.mutate(patientLookupResults.patient.address, {
			onSuccess: geocodedAddress => {
				formHelpers.setFieldValue('address', geocodedAddress);
			}
		});
	};

	return (
		<PatientDataContext.Provider value={patientLookupResults?.legacyChart}>
			<div className="flex items-end gap-4">
				<TextFormField className="grow" name="mrNumber" label="MR Number" onChangePassThrough={onMrNumberChange} />
				<Button
					isPending={isLookingUp}
					isDisabled={!formHelpers.values.mrNumber || formHelpers.values.mrNumber.length < 6}
					Icon={Search}
					label="Lookup Patient"
					className="h-[42px]"
					onClick={() => onPatientLookup(formHelpers.values.mrNumber!)}
				/>
			</div>
			{(patientLookupStatus === 'not-found' || patientLookupStatus === 'error') && (
				<div
					className={clsx(
						'rounded-lg border p-4',
						patientLookupStatus === 'not-found'
							? 'border-yellow-200 bg-yellow-50 text-yellow-700'
							: 'border-red-200 bg-red-50 text-red-700'
					)}
				>
					<div className={clsx('mb-2 flex items-center gap-2', patientLookupStatus === 'not-found' ? 'text-yellow-700' : 'text-red-700')}>
						<MapPin className="size-5" />
						<h3 className="font-medium">No Patient Found:</h3>
					</div>
					<p className="text-sm">
						{patientLookupStatus === 'error'
							? 'An error occurred while looking up the patient. Please try again.'
							: `No patient was found with the MR number ${patientLookupMR}. If the patient is not new please verify the MR number and try again, or enter the patient's information manually.`}
					</p>
				</div>
			)}
			<div className="grid grid-cols-2 gap-4">
				<TextFormField name="patientFirstName" isDisabled={patientLookupStatus === 'found'} />
				<TextFormField name="patientLastName" isDisabled={patientLookupStatus === 'found'} />
			</div>
			{patientLookupResults?.patient?.address && (
				<div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-4 py-3">
					<div className="flex items-center gap-2">
						<MapPin className="size-4 shrink-0 text-green-700" />
						<span className="text-sm text-gray-700">
							<span className="font-medium text-gray-900">Current Address: </span>
							{patientLookupResults.patient.address.addressOne}, {patientLookupResults.patient.address.city},{' '}
							{patientLookupResults.patient.address.state} {patientLookupResults.patient.address.zipCode}
						</span>
					</div>
					<Button
						isPending={geocode.isPending}
						appearance="outline"
						className="ml-4 shrink-0 border-green-700 px-3 py-1.5 text-sm text-green-700 hover:bg-green-700 hover:text-white"
						onClick={onUseExistingAddress}
						label="Use This Address"
						Icon={MapPin}
					/>
				</div>
			)}
			<AddressFormField name="address" />
			{children}
		</PatientDataContext.Provider>
	);
}
